export type BentoPersonaId =
    | "area_ops"
    | "store_manager"
    | "marketing"
    | "super_user"
    | "no_coaches_enabled";

export interface BentoPersona {
    id: BentoPersonaId;
    name: string;
    title: string;
    description: string;
    accent: string;
    defaultCoachId: string;
    visibleCoachIds: string[];
    visibleInsightIds: string[];
    briefingStyle: string;
    intelligenceRole: string;
    suggestionHint: string;
    subtitleDomains: string;
}

export const PERSONA_STORAGE_KEY = "parIntelligenceDemoPersona";

const MARKETING_INSIGHT_IDS = [
    "ins_enrollment_drop",
    "ins_win_back",
    "ins_silver_tier",
    "ins_offer_incrementality",
    "ins_demo_promotion_effectiveness",
    "ins_demo_loyalty_churn"
];

const IN_STORE_INSIGHT_IDS = [
    "ins_cogs_variance_trend",
    "ins_cogs_classification",
    "ins_cogs_recovery",
    "ins_labor_dinner_overtime",
    "ins_labor_live_staffing",
    "ins_labor_store_outliers",
    "ins_sos_peak_spike",
    "ins_sos_trend_slowing",
    "ins_sos_thursday_staffing",
    "ins_sos_lunch_daypart",
    "ins_sos_store_outliers",
    "ins_demo_high_food_cost",
    "ins_demo_store_health",
    "ins_demo_menu_mix_labor"
];

const ABOVE_STORE_INSIGHT_IDS = [...IN_STORE_INSIGHT_IDS, "ins_cogs_top_offender"];

const ALL_INSIGHT_IDS = [...MARKETING_INSIGHT_IDS, ...IN_STORE_INSIGHT_IDS];

export const BENTO_PERSONAS: BentoPersona[] = [
    {
        id: "area_ops",
        name: "Above Store Operations",
        title: "Above Store Operations Manager",
        description:
            "Oversees multiple locations, staffing performance, and operational consistency.",
        accent: "#5f7cff",
        defaultCoachId: "operate",
        visibleCoachIds: ["operate", "sell", "restaurantCore"],
        visibleInsightIds: ABOVE_STORE_INSIGHT_IDS,
        briefingStyle:
            "Prioritize multi-location operational variance, staffing effectiveness, and execution risks.",
        intelligenceRole: "Operations",
        suggestionHint: "Labor, drive-thru and inventory",
        subtitleDomains: "labor, drive-thru speed, kitchen throughput, or inventory"
    },
    {
        id: "store_manager",
        name: "In-Store Operations",
        title: "In-Store Manager",
        description:
            "Runs day-to-day service quality, labor balance, and shift execution in one location.",
        accent: "#ffad42",
        defaultCoachId: "operate",
        visibleCoachIds: ["operate", "sell", "restaurantCore"],
        visibleInsightIds: IN_STORE_INSIGHT_IDS,
        briefingStyle:
            "Prioritize immediate actions for shift execution, throughput, labor, and guest experience.",
        intelligenceRole: "Operations",
        suggestionHint: "Labor, drive-thru and inventory",
        subtitleDomains: "labor, drive-thru speed, kitchen throughput, or inventory"
    },
    {
        id: "marketing",
        name: "Corporate Marketing",
        title: "Corporate Marketing",
        description:
            "Owns campaign performance, loyalty growth, and cross-channel guest engagement.",
        accent: "#37d2f1",
        defaultCoachId: "engage",
        visibleCoachIds: ["engage"],
        visibleInsightIds: MARKETING_INSIGHT_IDS,
        briefingStyle:
            "Prioritize campaign effectiveness, loyalty conversion, and incremental guest revenue opportunities.",
        intelligenceRole: "Corporate Marketing",
        suggestionHint: "Loyalty, campaigns, and offers",
        subtitleDomains: "loyalty, campaigns, offers, and menu performance"
    },
    {
        id: "super_user",
        name: "Super User",
        title: "Super User",
        description:
            "Cross-functional operator mode with full coach visibility across all domains.",
        accent: "#4dd5c4",
        defaultCoachId: "operate",
        visibleCoachIds: ["sell", "operate", "engage", "restaurantCore"],
        visibleInsightIds: ALL_INSIGHT_IDS,
        briefingStyle:
            "Provide cross-role insights and include configuration options across all product areas.",
        intelligenceRole: "Corporate Marketing",
        suggestionHint: "Loyalty, campaigns, and offers",
        subtitleDomains: "loyalty, campaigns, offers, and menu performance"
    },
    {
        id: "no_coaches_enabled",
        name: "No Coaches Enabled",
        title: "No Coaches Enabled",
        description: "Demonstration mode with all coach surfaces hidden.",
        accent: "#8f98ad",
        defaultCoachId: "operate",
        visibleCoachIds: ["sell", "operate", "engage", "restaurantCore"],
        visibleInsightIds: ALL_INSIGHT_IDS,
        briefingStyle:
            "Do not include coach-specific recommendations because no coaches are enabled.",
        intelligenceRole: "Corporate Marketing",
        suggestionHint: "How the platform works",
        subtitleDomains: "platform setup, campaigns, and reporting in Punchh"
    }
];

export const DEFAULT_BENTO_PERSONA_ID: BentoPersonaId = "area_ops";

export const getPersonaFriendlyName = (persona: BentoPersona): string =>
    persona.intelligenceRole;

export const getBentoPersonaById = (id: string | null | undefined): BentoPersona =>
    BENTO_PERSONAS.find((persona) => persona.id === id) ?? BENTO_PERSONAS[0];

export const readStoredBentoPersonaId = (): BentoPersonaId => {
    if (typeof window === "undefined") {
        return DEFAULT_BENTO_PERSONA_ID;
    }

    const storedValue = window.localStorage.getItem(PERSONA_STORAGE_KEY);
    return getBentoPersonaById(storedValue).id;
};

export const storeBentoPersonaId = (id: BentoPersonaId): void => {
    if (typeof window === "undefined") {
        return;
    }
    window.localStorage.setItem(PERSONA_STORAGE_KEY, id);
};

export const buildPersonaPromptContext = (
    prompt: string,
    persona: BentoPersona
): string =>
    `${prompt}\n\nPersona context: I am viewing the dashboard as ${persona.title}. ${persona.briefingStyle}`;
